import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
} from "@mui/material";
import API_BASE_URL from "../../utils/api";

import API_URL from "../../config";
import { useAuth } from "../../components/Context/AuthContext";
import Banner from "../../assets/Banner.png";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IconButton, Menu, MenuItem } from "@mui/material";
const formatDisplayDate = (value) => {
  if (!value) return "";

  const iso = typeof value === "string" ? value : new Date(value).toISOString();

  const [year, month, day] = iso.slice(0, 10).split("-").map(Number); // Create a *local* date with that Y/M/D so timezone won't shift it

  const localDate = new Date(year, month - 1, day);

  return localDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};
const BlogActions = ({ blog, user, onDeleted, navigate }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleEdit = () => {
    handleMenuClose();
    navigate(`/blogs/edit/${blog._id}`);
  };
  const handleDeleteClick = () => {
    setConfirmOpen(true);
  };
  const handleDeleteConfirm = async () => {
    setConfirmOpen(false);

    try {
      await axios.delete(`${API_BASE_URL}/blogs/${blog._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      onDeleted?.();
      navigate(`/blogs`);
    } catch (error) {
      console.error("Delete failed", error);
      alert("Failed to delete blog");
    }
  };

  if (!(user?.role === "admin" || user?._id === blog.author?._id)) return null;

  return (
    <>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          handleMenuOpen(e);
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleEdit();
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleMenuClose();
            handleDeleteClick();
          }}
        >
          Delete
        </MenuItem>
      </Menu>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete Blog</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this blog? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
const BlogCard = ({ item, onClick, user, navigate, onDeleted }) => {
  const defaultBanner = Banner;
  const contentImage = (item.content || "").match(
    /<img.*?src=["'](.*?)["']/
  )?.[1];
  const imageSrc = item.imageUrl || contentImage || defaultBanner;

  const displayDate = item.externalPublishedAt || item.createdAt;
  const cleanText = (item.content || "").replace(/<[^>]+>/g, "");
  const isExternalNews = item.type === "news" && item.isExternal;
  const overlayLabel = item.type === "news" ? "View news" : "View blog";

  const handleClick = () => {
    if (isExternalNews && item.externalUrl) {
      window.open(item.externalUrl, "_blank", "noopener,noreferrer");
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        maxWidth: 300,
        width: "100%",
        margin: "0 auto",
        cursor: "pointer",
        backgroundColor: "#fff",
        borderRadius: "8px",
        p: 3,
        boxShadow: 2,
        transition: "transform 0.2s ease,box-shadow 0.3s ease",
        "&:hover": { boxShadow: 4, transform: "translateY(-4px)" },
        textAlign: "left",
        position: "relative",
        overflow: "hidden",
        "&:hover .blog-card-overlay": { opacity: 1 },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {displayDate ? formatDisplayDate(displayDate) : ""}
        </Typography>
        <BlogActions
          blog={item}
          user={user}
          navigate={navigate}
          onDeleted={onDeleted}
        />
      </Box>
      <img
        src={imageSrc}
        alt="blog-preview"
        style={{
          width: "100%",
          height: 180,
          objectFit: "contain",
          marginBottom: 8,
          borderRadius: 6,
          display: "block",
          marginInline: "auto",
        }}
        onError={(e) => {
          e.currentTarget.src = contentImage || defaultBanner;
        }}
      />
      <Typography
        variant="h6"
        sx={{
          mb: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          height: "48px",
          lineHeight: "1.2em",
        }}
      >
        {item.title}
      </Typography>
      {!isExternalNews && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
          }}
        >
          {cleanText}... <em>continued</em>
        </Typography>
      )}
      <Box
        className="blog-card-overlay"
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.05), rgba(0,0,0,0.55))",
          opacity: 0,
          transition: "opacity 0.25s ease",
          pointerEvents: "none",
        }}
      >
        <Typography
          variant="button"
          sx={{
            px: 2.5,
            py: 0.75,
            borderRadius: 999,
            bgcolor: "rgba(255,255,255,0.94)",
            color: "#000",
            fontWeight: 600,
            letterSpacing: 0.5,
            border: "1px solid #ffd700",
            boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
            textTransform: "uppercase",
            fontSize: 12,
          }}
        >
          {overlayLabel}
        </Typography>
      </Box>
    </Box>
  );
};

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();
  const { user } = useAuth();

  const loadBlogs = () => {
    axios
      .get(`${API_BASE_URL}/blogs`)
      .then((res) => setBlogs(res.data))
      .catch(console.error);
  };
  useEffect(() => {
    loadBlogs();
  }, []);

  const sortByDisplayDateDesc = (a, b) => {
    const da = new Date(a.externalPublishedAt || a.createdAt).getTime();
    const db = new Date(b.externalPublishedAt || b.createdAt).getTime();
    return db - da;
  };

  const filtered = [...blogs]
    .filter((b) => filter === "all" || b.type === filter)
    .sort(sortByDisplayDateDesc);
  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {user?.role === "admin" && (
        <>
          <Box
            sx={{
              display: { xs: "none", sm: "block" },
              textAlign: "right",
              mb: 2,
            }}
          >
            <Button
              variant="contained"
              onClick={() => navigate("/blogs/create")}
            >
              + Create Blog / News
            </Button>
          </Box>
          <Box
            sx={{
              display: { xs: "block", sm: "none" },
              position: "fixed",
              bottom: 16,
              right: 16,
            }}
          >
            <Button
              variant="contained"
              onClick={() => navigate("/blogs/create")}
            >
              +
            </Button>
          </Box>
        </>
      )}
      <Box sx={{ display: "flex", gap: 2, mb: 4, justifyContent: "center" }}>
        <Chip
          label="All"
          onClick={() => setFilter("all")}
          color={filter === "all" ? "primary" : "default"}
        />
        <Chip
          label="Blogs"
          onClick={() => setFilter("blog")}
          color={filter === "blog" ? "primary" : "default"}
        />
        <Chip
          label="News"
          onClick={() => setFilter("news")}
          color={filter === "news" ? "primary" : "default"}
        />
      </Box>

      {filtered.length === 0 ? (
        <Typography variant="h6" align="center" color="text.secondary">
          {filter === "blog"
            ? "No blogs available."
            : filter === "news"
              ? "No news articles available."
              : "No blogs or news posts yet."}
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
              lg: "1fr 1fr 1fr 1fr",
            },
            gap: 3,
          }}
        >
          {filtered.map((item, idx) => (
            <Box key={idx}>
              <BlogCard
                item={item}
                user={user}
                navigate={navigate}
                onDeleted={loadBlogs}
                onClick={() => navigate(`/blogs/${item._id}`)}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Blogs;
